import { educationInfoInit, workExperienceInit } from './constants';
import { signUpFlow } from '../../config';
import {
    kycThunk,
    signUpThunk,
    confirmSignUpThunk,
    updatePersonalInfoThunk,
    getCitiesThunk,
    getStatesThunk,
    setProfileDataThunk,
    getCountriesThunk,
    getExpertiesThunk,
    getSkillsThunk,
    getUniversitiesThunk,
    getJobListThunk,
    checkSignUpThunk,
    confirmPhoneNumberThunk,
    resendEmailConfirmationCodeThunk,
} from './thunks';
import {
    nanoid,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import {
    State,
    EducationInfo,
    PersonalInfo,
    SignUpStep,
    WorkExperience,
    ExpertiseAndSkills,
    VisaAndSalaryRange,
    FetchStatus,
    KYCStatus,
    SignUpSupplementaryStep,
} from './types';

const initialState: State = {
    jobList: [],
    userEmail: '',
    IDPhoto: null,
    experties: [],
    citiesList: [],
    statesList: [],
    skillsList: [],
    phoneNumber: '',
    editMode: false,
    expertsList: [],
    countriesList: [],
    userId: undefined,
    legalModal: false,
    personalInfo: null,
    personalPhoto: null,
    fetchStatus: 'Idle',
    universitiesList: [],
    legalAgreement: false,
    KYC_Status: 'Pending',
    signUpSteps: signUpFlow,
    visaAndSalaryRange: null,
    disablePhoneNumber: false,
    signUpStep: 'registerEmail',
    termsAndConditionsModal: false,
    educationInfo: educationInfoInit,
    workExperience: workExperienceInit,
    signUpSupplementaryStep: 'personalInfo',
};

export const signUpSlice = createSlice({
    name: 'signUp',
    initialState,
    reducers: {
        setSignUpStep: (state, action: PayloadAction<SignUpStep>) => {
            state.signUpStep = action.payload;
        },
        setSignUpSteps: (state, action: PayloadAction<SignUpStep[]>) => {
            state.signUpSteps = action.payload;
        },
        setSignUpUserId: (state, action: PayloadAction<string | undefined>) => {
            state.userId = action.payload;
        },
        setEditMode: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload;
        },
        setPhoneNumber: (state, action: PayloadAction<string>) => {
            state.phoneNumber = action.payload;
        },
        setUserEmail: (state, action: PayloadAction<string>) => {
            state.userEmail = action.payload;
        },
        setPersonalInfo: (state, action: PayloadAction<PersonalInfo | null>) => {
            state.personalInfo = action.payload;
        },
        setPersonalPhoto: (state, action: PayloadAction<string | null>) => {
            state.personalPhoto = action.payload;
        },
        setIDPhoto: (state, action: PayloadAction<string | null>) => {
            state.IDPhoto = action.payload;
        },
        setExperties: (state, action: PayloadAction<ExpertiseAndSkills[]>) => {
            state.experties = action.payload;
        },
        setVisaAndSalaryRange: (state, action: PayloadAction<VisaAndSalaryRange | null>) => {
            state.visaAndSalaryRange = action.payload;
        },
        setFetchStatus: (state, action: PayloadAction<FetchStatus>) => {
            state.fetchStatus = action.payload;
        },
        setKYCStatus: (state, action: PayloadAction<KYCStatus>) => {
            state.KYC_Status = action.payload;
        },
        setEducationInfo: (state, action: PayloadAction<EducationInfo | undefined>) => {
            if (!action.payload) {
                const id = state.educationInfo.length === 0 ? 'initial-record' : nanoid();
                state.educationInfo.push({
                    id,
                    universityName: '',
                    universityId: '0',
                    degree: '',
                    field: '',
                    GPA: '',
                    from: '',
                    to: '',
                    didNotFinishDegree: false,
                });
            } else {
                state.educationInfo.push(action.payload);
            }
        },
        setWorkExperience: (state, action: PayloadAction<WorkExperience | undefined>) => {
            if (!action.payload) {
                const id = state.workExperience.length === 0 ? 'initial-record' : nanoid();
                state.workExperience.push({
                    id,
                    company: '',
                    roleDescription: '',
                    jobTitle: '',
                    location: '',
                    from: '',
                    to: '',
                    currentlyworkHere: false,
                });
            } else {
                state.workExperience.push(action.payload);
            }
        },
        updateWorkExperience: (state, action: PayloadAction<WorkExperience>) => {
            const indx = state.workExperience.findIndex(i => i.id === action.payload.id);
            state.workExperience[indx] = action.payload;
        },
        removeWorkExperience: (state, action: PayloadAction<string>) => {
            state.workExperience = state.workExperience.filter(i => i.id !== action.payload);
        },
        resetWorkExperience: (state) => {
            state.workExperience = [];
        },
        resetEducationInfo: (state) => {
            state.educationInfo = [];
        },
        updateEducationInfo: (state, action: PayloadAction<EducationInfo>) => {
            const indx = state.educationInfo.findIndex(i => i.id === action.payload.id);
            state.educationInfo[indx] = action.payload;
        },
        removeEducationInfo: (state, action: PayloadAction<string>) => {
            state.educationInfo = state.educationInfo.filter(i => i.id !== action.payload);
        },
        setTermsAndConditionsModal: (state, action: PayloadAction<boolean>) => {
            state.termsAndConditionsModal = action.payload;
        },
        setLegalAgreement: (state, action: PayloadAction<boolean>) => {
            state.legalAgreement = action.payload;
        },
        setLegalModal: (state, action: PayloadAction<boolean>) => {
            state.legalModal = action.payload;
        },
        setPhoneNumberDisable: (state, action: PayloadAction<boolean>) => {
            state.disablePhoneNumber = action.payload;
        },
        setSignUpSupplementaryStep: (state, action: PayloadAction<SignUpSupplementaryStep>) => {
            state.signUpSupplementaryStep = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signUpThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(signUpThunk.fulfilled, (state, action) => {
                state.userId = action.payload;
                state.fetchStatus = 'Idle';
                state.signUpStep = 'verifyEmail';
            })
            .addCase(signUpThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(confirmSignUpThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(confirmSignUpThunk.fulfilled, (state) => {
                state.fetchStatus = 'Idle';
            })
            .addCase(confirmSignUpThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(updatePersonalInfoThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(updatePersonalInfoThunk.fulfilled, (state) => {
                state.fetchStatus = 'Idle';
            })
            .addCase(updatePersonalInfoThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(confirmPhoneNumberThunk.fulfilled, (state) => {
                state.signUpStep = 'uploadProfilePhoto';
                state.disablePhoneNumber = true;
                state.fetchStatus = 'Idle';
            })
            .addCase(getCountriesThunk.fulfilled, (state, action) => {
                state.countriesList = action.payload;
            })
            .addCase(getStatesThunk.fulfilled, (state, action) => {
                state.statesList = action.payload;
            })
            .addCase(getCitiesThunk.fulfilled, (state, action) => {
                state.citiesList = action.payload;
            })
            .addCase(setProfileDataThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(setProfileDataThunk.fulfilled, (state) => {
                state.fetchStatus = 'Idle';
                state.signUpStep = 'approval';
            })
            .addCase(setProfileDataThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(getExpertiesThunk.fulfilled, (state, action) => {
                state.expertsList = action.payload;
            })
            .addCase(getSkillsThunk.fulfilled, (state, action) => {
                state.skillsList = action.payload;
            })
            .addCase(getUniversitiesThunk.fulfilled, (state, action) => {
                state.universitiesList = action.payload;
            })
            .addCase(getJobListThunk.fulfilled, (state, action) => {
                state.jobList = action.payload;
            })
            .addCase(kycThunk.fulfilled, (state) => {
                state.KYC_Status = 'Approved';
                if (state.editMode) {
                    state.signUpStep = 'reviewInfo';
                } else {
                    state.signUpStep = 'workExperience';
                }
            })
            .addCase(kycThunk.rejected, (state) => {
                state.KYC_Status = 'Reject';
            })
            .addCase(checkSignUpThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(checkSignUpThunk.rejected, (state) => {
                state.fetchStatus = 'Idle';
            })
            .addCase(resendEmailConfirmationCodeThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(resendEmailConfirmationCodeThunk.fulfilled, (state) => {
                state.signUpStep = 'verifyEmail';
                state.fetchStatus = 'Idle';
            })
            .addCase(resendEmailConfirmationCodeThunk.rejected, (state) => {
                state.fetchStatus = 'Idle';
            })
    }
})

export const {
    setUserEmail,
    setKYCStatus,
    setPhoneNumberDisable,
    setSignUpUserId,
    setVisaAndSalaryRange,
    setSignUpStep,
    setFetchStatus,
    setSignUpSteps,
    updateEducationInfo,
    updateWorkExperience,
    removeWorkExperience,
    setExperties,
    resetEducationInfo,
    resetWorkExperience,
    removeEducationInfo,
    setPersonalPhoto,
    setIDPhoto,
    setEditMode,
    setLegalModal,
    setLegalAgreement,
    setPhoneNumber,
    setPersonalInfo,
    setEducationInfo,
    setWorkExperience,
    setTermsAndConditionsModal,
    setSignUpSupplementaryStep,
} = signUpSlice.actions;